import React, { useState, useEffect } from 'react';
import { Lock, Plus, Edit2, Trash2, Save, X, Check, Users, ShoppingCart, TrendingUp, Calendar, Mail, Phone, MapPin, DollarSign, Package, Filter, Search, Download, Eye } from 'lucide-react';
import { supabase } from '../../lib/supabase';

interface AdminPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

interface SubscriptionOption {
  id: string;
  months: number;
  monthly_price: number | null;
  upfront_price: number | null;
  single_price: number | null;
  bags: number;
  size_oz: number;
  label: string;
  savings_text: string;
  popular: boolean;
  active: boolean;
  is_subscription: boolean;
}

interface Order {
  id: string;
  email: string;
  name: string;
  phone: string;
  address: string;
  city: string;
  state: string;
  zip_code: string;
  roast_type: string;
  purchase_type: string;
  months: number;
  bags: number;
  size_oz: number;
  total_due: number;
  status: string;
  payment_status: string;
  created_at: string;
  updated_at: string;
}

interface Subscription {
  id: string;
  email: string;
  roast_type: string;
  subscription_months: number;
  status: string;
  quiz_completed: boolean;
  created_at: string;
  updated_at: string;
}

interface Lead {
  id: string;
  email: string;
  roast_type: string | null;
  status: string;
  quiz_completed: boolean;
  email_captured_at: string | null;
  created_at: string;
  days_since_capture: number;
}

const AdminPanel: React.FC<AdminPanelProps> = ({ isOpen, onClose }) => {
  const [password, setPassword] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState<'dashboard' | 'orders' | 'customers' | 'leads' | 'options'>('dashboard');
  
  // Data states
  const [orders, setOrders] = useState<Order[]>([]);
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [leads, setLeads] = useState<Lead[]>([]);
  const [filteredLeads, setFilteredLeads] = useState<Lead[]>([]);
  const [leadFilter, setLeadFilter] = useState<'all' | '1day' | '7days' | '30days'>('all');
  const [options, setOptions] = useState<SubscriptionOption[]>([]);
  const [loading, setLoading] = useState(false);
  
  // Filter states
  const [orderFilter, setOrderFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [dateRange, setDateRange] = useState('all');
  
  // Option management states
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);

  const correctPassword = 'bondcoffee2024';

  const defaultOption: Omit<SubscriptionOption, 'id'> = {
    months: 3,
    monthly_price: null,
    upfront_price: null,
    single_price: null,
    bags: 1,
    size_oz: 12,
    label: '',
    savings_text: '',
    popular: false,
    active: true,
    is_subscription: true
  };

  const [newOption, setNewOption] = useState(defaultOption);
  const [editOption, setEditOption] = useState<SubscriptionOption | null>(null);

  const loadLeads = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('subscriptions')
        .select('*')
        .is('completed_at', null)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      
      const leadsData: Lead[] = (data || []).map(sub => ({
        id: sub.id,
        email: sub.email,
        created_at: sub.created_at || '',
        quiz_completed: sub.quiz_completed || false,
        roast_type: sub.roast_type,
        status: sub.status || 'email_captured'
      }));
      
      setLeads(leadsData);
    } catch (err) {
      console.error('Error loading leads:', err);
      setError('Failed to load leads');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      loadAllData();
    }
    loadLeads();
  }, [isAuthenticated]);

  if (!isOpen) return null;

  const handleLogin = () => {
    if (password === correctPassword) {
      setIsAuthenticated(true);
      setError('');
    } else {
      setError('Incorrect password');
    }
  };

  const loadAllData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadOrders(),
        loadSubscriptions(),
        loadOptions()
      ]);
    } catch (err) {
      console.error('Error loading data:', err);
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const loadOrders = async () => {
    try {
      const { data, error } = await supabase
        .from('orders')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      setOrders(data || []);
    } catch (err) {
      console.error('Error loading orders:', err);
    }
  };

  const loadSubscriptions = async () => {
    try {
      const { data, error } = await supabase
        .from('subscriptions')
        .select('*')
        .in('status', ['quiz_completed', 'active', 'paused', 'cancelled'])
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      setSubscriptions(data || []);
    } catch (err) {
      console.error('Error loading subscriptions:', err);
    }
  };

  const loadOptions = async () => {
    try {
      const { data, error } = await supabase
        .from('subscription_options')
        .select('*')
        .order('is_subscription', { ascending: false })
        .order('months');
      
      if (error) throw error;
      setOptions(data || []);
    } catch (err) {
      console.error('Error loading options:', err);
    }
  };

  const updateOrderStatus = async (orderId: string, status: string) => {
    try {
      const { error } = await supabase
        .from('orders')
        .update({ status })
        .eq('id', orderId);

      if (error) throw error;
      await loadOrders();
    } catch (err) {
      console.error('Error updating order status:', err);
      setError('Failed to update order status');
    }
  };

  const updatePaymentStatus = async (orderId: string, paymentStatus: string) => {
    try {
      const { error } = await supabase
        .from('orders')
        .update({ payment_status: paymentStatus })
        .eq('id', orderId);

      if (error) throw error;
      await loadOrders();
    } catch (err) {
      console.error('Error updating payment status:', err);
      setError('Failed to update payment status');
    }
  };

  // Analytics calculations
  const analytics = {
    totalOrders: orders.length,
    totalRevenue: orders.reduce((sum, order) => sum + (order.total_due || 0), 0),
    pendingOrders: orders.filter(o => o.status === 'pending').length,
    completedOrders: orders.filter(o => o.status === 'confirmed' || o.status === 'delivered').length,
    totalCustomers: new Set([...orders.map(o => o.email), ...subscriptions.map(s => s.email)]).size,
    subscriptionLeads: subscriptions.filter(s => s.status === 'email_captured' || s.status === 'quiz_completed').length,
    conversionRate: orders.length > 0 ? ((orders.length / subscriptions.length) * 100).toFixed(1) : '0'
  };

  const formatPrice = (cents: number | null) => {
    if (cents === null || cents === undefined) return '$0.00';
    return `$${(cents / 100).toFixed(2)}`;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getFilteredOrders = () => {
    let filtered = orders;

    // Status filter
    if (orderFilter !== 'all') {
      filtered = filtered.filter(order => order.status === orderFilter);
    }

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(order => 
        order.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        order.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        order.phone.includes(searchTerm)
      );
    }

    // Date filter
    if (dateRange !== 'all') {
      const now = new Date();
      const filterDate = new Date();
      
      switch (dateRange) {
        case 'today':
          filterDate.setHours(0, 0, 0, 0);
          break;
        case 'week':
          filterDate.setDate(now.getDate() - 7);
          break;
        case 'month':
          filterDate.setMonth(now.getMonth() - 1);
          break;
      }
      
      filtered = filtered.filter(order => new Date(order.created_at) >= filterDate);
    }

    return filtered;
  };

  if (!isAuthenticated) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
        <div className="bg-[#132947] p-8 rounded-lg max-w-md w-full">
          <div className="flex items-center gap-4 mb-6">
            <Lock className="w-8 h-8 text-[#fda36b]" />
            <h2 className="text-2xl font-bold text-white">Admin Access</h2>
          </div>
          
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleLogin()}
            placeholder="Enter password"
            className="w-full px-4 py-2 bg-white/10 rounded-lg mb-4 text-white placeholder-white/50"
          />
          
          {error && (
            <p className="text-red-400 text-sm mb-4">{error}</p>
          )}
          
          <div className="flex gap-4">
            <button
              onClick={handleLogin}
              className="flex-1 bg-[#fda36b] text-[#132947] px-4 py-2 rounded-lg font-semibold hover:bg-[#fda36b]/90"
            >
              Login
            </button>
            <button
              onClick={onClose}
              className="flex-1 bg-white/10 text-white px-4 py-2 rounded-lg hover:bg-white/20"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50 overflow-y-auto">
      <div className="bg-[#132947] rounded-lg max-w-7xl w-full max-h-[95vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-white/10">
          <h2 className="text-3xl font-bold text-white">Bond Coffee Admin</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
          >
            <X className="w-6 h-6 text-white" />
          </button>
        </div>

        {/* Navigation Tabs */}
        <div className="flex border-b border-white/10">
          {[
            { id: 'dashboard', label: 'Dashboard', icon: TrendingUp },
            { id: 'orders', label: 'Orders', icon: ShoppingCart },
            { id: 'customers', label: 'Customers', icon: Users },
            { id: 'options', label: 'Products', icon: Package }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`flex items-center gap-2 px-6 py-4 font-semibold transition-colors ${
                activeTab === tab.id
                  ? 'text-[#fda36b] border-b-2 border-[#fda36b]'
                  : 'text-white/70 hover:text-white'
              }`}
            >
              <tab.icon className="w-5 h-5" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content Area */}
        <div className="flex-1 overflow-y-auto p-6">
          {error && (
            <div className="bg-red-500/10 text-red-400 p-4 rounded-lg mb-6 border border-red-500/20">
              <p>{error}</p>
            </div>
          )}

          {loading && (
            <div className="text-center py-8">
              <div className="animate-spin w-8 h-8 border-2 border-[#fda36b] border-t-transparent rounded-full mx-auto mb-4"></div>
              <p className="text-white/80">Loading data...</p>
            </div>
          )}

          {/* Dashboard Tab */}
          {activeTab === 'dashboard' && (
            <div className="space-y-6">
              {/* Analytics Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="bg-gradient-to-br from-blue-500/20 to-blue-600/10 p-6 rounded-lg border border-blue-500/20">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-blue-400 text-sm font-semibold">Total Orders</p>
                      <p className="text-3xl font-bold text-white">{analytics.totalOrders}</p>
                    </div>
                    <ShoppingCart className="w-8 h-8 text-blue-400" />
                  </div>
                </div>

                <div className="bg-gradient-to-br from-green-500/20 to-green-600/10 p-6 rounded-lg border border-green-500/20">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-green-400 text-sm font-semibold">Total Revenue</p>
                      <p className="text-3xl font-bold text-white">{formatPrice(analytics.totalRevenue)}</p>
                    </div>
                    <DollarSign className="w-8 h-8 text-green-400" />
                  </div>
                </div>

                <div className="bg-gradient-to-br from-[#fda36b]/20 to-[#fda36b]/10 p-6 rounded-lg border border-[#fda36b]/20">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-[#fda36b] text-sm font-semibold">Total Customers</p>
                      <p className="text-3xl font-bold text-white">{analytics.totalCustomers}</p>
                    </div>
                    <Users className="w-8 h-8 text-[#fda36b]" />
                  </div>
                </div>

                <div className="bg-gradient-to-br from-purple-500/20 to-purple-600/10 p-6 rounded-lg border border-purple-500/20">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-purple-400 text-sm font-semibold">Conversion Rate</p>
                      <p className="text-3xl font-bold text-white">{analytics.conversionRate}%</p>
                    </div>
                    <TrendingUp className="w-8 h-8 text-purple-400" />
                  </div>
                </div>
              </div>

              {/* Recent Activity */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-white/5 rounded-lg p-6 border border-white/10">
                  <h3 className="text-xl font-semibold text-white mb-4">Recent Orders</h3>
                  <div className="space-y-3">
                    {orders.slice(0, 5).map(order => (
                      <div key={order.id} className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                        <div>
                          <p className="text-white font-medium">{order.name}</p>
                          <p className="text-white/60 text-sm">{order.roast_type} - {formatPrice(order.total_due)}</p>
                        </div>
                        <span className={`px-2 py-1 rounded text-xs font-semibold ${
                          order.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
                          order.status === 'confirmed' ? 'bg-green-500/20 text-green-400' :
                          'bg-gray-500/20 text-gray-400'
                        }`}>
                          {order.status}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-white/5 rounded-lg p-6 border border-white/10">
                  <h3 className="text-xl font-semibold text-white mb-4">Order Status Breakdown</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-white/80">Pending</span>
                      <span className="text-yellow-400 font-semibold">{analytics.pendingOrders}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-white/80">Completed</span>
                      <span className="text-green-400 font-semibold">{analytics.completedOrders}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-white/80">Subscription Leads</span>
                      <span className="text-blue-400 font-semibold">{analytics.subscriptionLeads}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Orders Tab */}
          {activeTab === 'orders' && (
            <div className="space-y-6">
              {/* Filters */}
              <div className="flex flex-wrap gap-4 items-center">
                <div className="flex items-center gap-2">
                  <Filter className="w-5 h-5 text-white/60" />
                  <select
                    value={orderFilter}
                    onChange={(e) => setOrderFilter(e.target.value)}
                    className="bg-white/10 text-white px-3 py-2 rounded-lg border border-white/20"
                  >
                    <option value="all">All Orders</option>
                    <option value="pending">Pending</option>
                    <option value="confirmed">Confirmed</option>
                    <option value="processing">Processing</option>
                    <option value="shipped">Shipped</option>
                    <option value="delivered">Delivered</option>
                  </select>
                </div>

                <div className="flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-white/60" />
                  <select
                    value={dateRange}
                    onChange={(e) => setDateRange(e.target.value)}
                    className="bg-white/10 text-white px-3 py-2 rounded-lg border border-white/20"
                  >
                    <option value="all">All Time</option>
                    <option value="today">Today</option>
                    <option value="week">Last Week</option>
                    <option value="month">Last Month</option>
                  </select>
                </div>

                <div className="flex items-center gap-2 flex-1 max-w-md">
                  <Search className="w-5 h-5 text-white/60" />
                  <input
                    type="text"
                    placeholder="Search customers..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="flex-1 bg-white/10 text-white px-3 py-2 rounded-lg border border-white/20 placeholder-white/50"
                  />
                </div>
              </div>

              {/* Orders Table */}
              <div className="bg-white/5 rounded-lg overflow-hidden border border-white/10">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-white/10">
                      <tr>
                        <th className="px-4 py-3 text-left text-white font-semibold">Customer</th>
                        <th className="px-4 py-3 text-left text-white font-semibold">Order Details</th>
                        <th className="px-4 py-3 text-left text-white font-semibold">Amount</th>
                        <th className="px-4 py-3 text-left text-white font-semibold">Status</th>
                        <th className="px-4 py-3 text-left text-white font-semibold">Payment</th>
                        <th className="px-4 py-3 text-left text-white font-semibold">Date</th>
                        <th className="px-4 py-3 text-left text-white font-semibold">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {getFilteredOrders().map((order) => (
                        <tr key={order.id} className="border-t border-white/10 hover:bg-white/5">
                          <td className="px-4 py-3">
                            <div>
                              <div className="text-white font-medium">{order.name}</div>
                              <div className="text-white/60 text-sm">{order.email}</div>
                              <div className="text-white/60 text-sm">{order.phone}</div>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div>
                              <div className="text-white capitalize">{order.roast_type} roast</div>
                              <div className="text-white/60 text-sm">
                                {order.bags} bag{order.bags > 1 ? 's' : ''} × {order.size_oz}oz
                              </div>
                              <div className="text-white/60 text-sm capitalize">
                                {order.purchase_type}
                                {order.purchase_type === 'subscription' && ` (${order.months} months)`}
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="text-white font-semibold">
                              {formatPrice(order.total_due)}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <select
                              value={order.status}
                              onChange={(e) => updateOrderStatus(order.id, e.target.value)}
                              className={`px-2 py-1 rounded text-xs font-semibold border-0 ${
                                order.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
                                order.status === 'confirmed' ? 'bg-blue-500/20 text-blue-400' :
                                order.status === 'processing' ? 'bg-purple-500/20 text-purple-400' :
                                order.status === 'shipped' ? 'bg-orange-500/20 text-orange-400' :
                                order.status === 'delivered' ? 'bg-green-500/20 text-green-400' :
                                'bg-gray-500/20 text-gray-400'
                              }`}
                            >
                              <option value="pending">Pending</option>
                              <option value="confirmed">Confirmed</option>
                              <option value="processing">Processing</option>
                              <option value="shipped">Shipped</option>
                              <option value="delivered">Delivered</option>
                              <option value="cancelled">Cancelled</option>
                            </select>
                          </td>
                          <td className="px-4 py-3">
                            <select
                              value={order.payment_status}
                              onChange={(e) => updatePaymentStatus(order.id, e.target.value)}
                              className={`px-2 py-1 rounded text-xs font-semibold border-0 ${
                                order.payment_status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
                                order.payment_status === 'paid' ? 'bg-green-500/20 text-green-400' :
                                order.payment_status === 'failed' ? 'bg-red-500/20 text-red-400' :
                                'bg-gray-500/20 text-gray-400'
                              }`}
                            >
                              <option value="pending">Pending</option>
                              <option value="paid">Paid</option>
                              <option value="failed">Failed</option>
                              <option value="refunded">Refunded</option>
                            </select>
                          </td>
                          <td className="px-4 py-3 text-white/80 text-sm">
                            {formatDate(order.created_at)}
                          </td>
                          <td className="px-4 py-3">
                            <button
                              onClick={() => setSelectedOrder(order)}
                              className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                            >
                              <Eye className="w-4 h-4 text-white/60" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                
                {getFilteredOrders().length === 0 && (
                  <div className="p-8 text-center text-white/60">
                    No orders found matching your filters.
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Customers Tab */}
          {activeTab === 'customers' && (
            <div className="space-y-6">
              <div className="bg-white/5 rounded-lg overflow-hidden border border-white/10">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-white/10">
                      <tr>
                        <th className="px-4 py-3 text-left text-white font-semibold">Customer</th>
                        <th className="px-4 py-3 text-left text-white font-semibold">Status</th>
                        <th className="px-4 py-3 text-left text-white font-semibold">Preferences</th>
                        <th className="px-4 py-3 text-left text-white font-semibold">Orders</th>
                        <th className="px-4 py-3 text-left text-white font-semibold">Total Spent</th>
                        <th className="px-4 py-3 text-left text-white font-semibold">Joined</th>
                      </tr>
                    </thead>
                    <tbody>
                      {subscriptions.map((sub) => {
                        const customerOrders = orders.filter(o => o.email === sub.email);
                        const totalSpent = customerOrders.reduce((sum, order) => sum + (order.total_due || 0), 0);
                        
                        return (
                          <tr key={sub.id} className="border-t border-white/10 hover:bg-white/5">
                            <td className="px-4 py-3">
                              <div>
                                <div className="text-white font-medium">{sub.email}</div>
                                {customerOrders.length > 0 && (
                                  <div className="text-white/60 text-sm">{customerOrders[0].name}</div>
                                )}
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              <span className={`px-2 py-1 rounded text-xs font-semibold ${
                                sub.status === 'email_captured' ? 'bg-blue-500/20 text-blue-400' :
                                sub.status === 'quiz_completed' ? 'bg-purple-500/20 text-purple-400' :
                                sub.status === 'active' ? 'bg-green-500/20 text-green-400' :
                                'bg-gray-500/20 text-gray-400'
                              }`}>
                                {sub.status.replace('_', ' ')}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              <div className="text-white capitalize">{sub.roast_type} roast</div>
                              <div className="text-white/60 text-sm">
                                {sub.quiz_completed ? 'Quiz completed' : 'No quiz data'}
                              </div>
                            </td>
                            <td className="px-4 py-3 text-white">
                              {customerOrders.length}
                            </td>
                            <td className="px-4 py-3 text-white font-semibold">
                              {formatPrice(totalSpent)}
                            </td>
                            <td className="px-4 py-3 text-white/80 text-sm">
                              {formatDate(sub.created_at)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Products/Options Tab */}
          {activeTab === 'options' && (
            <ProductOptionsTab
              options={options}
              showCreateForm={showCreateForm}
              setShowCreateForm={setShowCreateForm}
              newOption={newOption}
              setNewOption={setNewOption}
              editingId={editingId}
              setEditingId={setEditingId}
              editOption={editOption}
              setEditOption={setEditOption}
              loading={loading}
              onCreateOption={async () => {
                setLoading(true);
                try {
                  const dataToSave = {
                    ...newOption,
                    monthly_price: newOption.is_subscription && newOption.monthly_price !== null ? Math.round(Number(newOption.monthly_price) * 100) : null,
                    single_price: !newOption.is_subscription && newOption.single_price !== null ? Math.round(Number(newOption.single_price) * 100) : null
                  };

                  const { error } = await supabase
                    .from('subscription_options')
                    .insert([dataToSave]);

                  if (error) throw error;

                  setNewOption(defaultOption);
                  setShowCreateForm(false);
                  await loadOptions();
                } catch (err) {
                  console.error('Error creating option:', err);
                  setError('Failed to create option');
                } finally {
                  setLoading(false);
                }
              }}
              onSaveOption={async () => {
                if (!editOption) return;
                
                setLoading(true);
                try {
                  const dataToSave = {
                    ...editOption,
                    monthly_price: editOption.is_subscription && editOption.monthly_price !== null ? Math.round(Number(editOption.monthly_price) * 100) : null,
                    single_price: !editOption.is_subscription && editOption.single_price !== null ? Math.round(Number(editOption.single_price) * 100) : null
                  };

                  const { error } = await supabase
                    .from('subscription_options')
                    .update(dataToSave)
                    .eq('id', editOption.id);

                  if (error) throw error;

                  setEditingId(null);
                  setEditOption(null);
                  await loadOptions();
                } catch (err) {
                  console.error('Error saving option:', err);
                  setError('Failed to save option');
                } finally {
                  setLoading(false);
                }
              }}
              onDeleteOption={async (id: string) => {
                if (!confirm('Are you sure you want to delete this option?')) return;
                
                setLoading(true);
                try {
                  const { error } = await supabase
                    .from('subscription_options')
                    .delete()
                    .eq('id', id);

                  if (error) throw error;
                  await loadOptions();
                } catch (err) {
                  console.error('Error deleting option:', err);
                  setError('Failed to delete option');
                } finally {
                  setLoading(false);
                }
              }}
              formatPrice={formatPrice}
            />
          )}
        </div>
      </div>

      {/* Order Detail Modal */}
      {selectedOrder && (
        <OrderDetailModal
          order={selectedOrder}
          onClose={() => setSelectedOrder(null)}
          formatPrice={formatPrice}
          formatDate={formatDate}
        />
      )}
    </div>
  );
};

// Product Options Tab Component
interface ProductOptionsTabProps {
  options: SubscriptionOption[];
  showCreateForm: boolean;
  setShowCreateForm: (show: boolean) => void;
  newOption: Omit<SubscriptionOption, 'id'>;
  setNewOption: (option: Omit<SubscriptionOption, 'id'>) => void;
  editingId: string | null;
  setEditingId: (id: string | null) => void;
  editOption: SubscriptionOption | null;
  setEditOption: (option: SubscriptionOption | null) => void;
  loading: boolean;
  onCreateOption: () => Promise<void>;
  onSaveOption: () => Promise<void>;
  onDeleteOption: (id: string) => Promise<void>;
  formatPrice: (cents: number | null) => string;
}

const ProductOptionsTab: React.FC<ProductOptionsTabProps> = ({
  options,
  showCreateForm,
  setShowCreateForm,
  newOption,
  setNewOption,
  editingId,
  setEditingId,
  editOption,
  setEditOption,
  loading,
  onCreateOption,
  onSaveOption,
  onDeleteOption,
  formatPrice
}) => {
  const handleEdit = (option: SubscriptionOption) => {
    setEditOption({
      ...option,
      monthly_price: option.monthly_price !== null ? Number((option.monthly_price / 100).toFixed(2)) : null,
      single_price: option.single_price !== null ? Number((option.single_price / 100).toFixed(2)) : null
    });
    setEditingId(option.id);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-xl font-semibold text-white">Product Options</h3>
        <button
          onClick={() => setShowCreateForm(true)}
          className="bg-[#fda36b] text-[#132947] px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-[#fda36b]/90 font-semibold"
        >
          <Plus className="w-4 h-4" />
          Add Option
        </button>
      </div>

      {showCreateForm && (
        <div className="bg-white/5 p-6 rounded-lg border border-white/10">
          <h4 className="text-lg font-semibold text-white mb-4">Create New Option</h4>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
            <div>
              <label className="block text-white text-sm mb-2">Type</label>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 text-white">
                  <input
                    type="radio"
                    checked={newOption.is_subscription}
                    onChange={() => setNewOption({ ...newOption, is_subscription: true })}
                    className="text-[#fda36b]"
                  />
                  Subscription
                </label>
                <label className="flex items-center gap-2 text-white">
                  <input
                    type="radio"
                    checked={!newOption.is_subscription}
                    onChange={() => setNewOption({ ...newOption, is_subscription: false })}
                    className="text-[#fda36b]"
                  />
                  Single Purchase
                </label>
              </div>
            </div>

            <div>
              <label className="block text-white text-sm mb-2">Months</label>
              <input
                type="number"
                value={newOption.months}
                onChange={(e) => setNewOption({ ...newOption, months: parseInt(e.target.value) || 0 })}
                className="w-full px-3 py-2 bg-white/10 rounded text-white border border-white/20"
              />
            </div>

            <div>
              <label className="block text-white text-sm mb-2">Bags</label>
              <input
                type="number"
                value={newOption.bags}
                onChange={(e) => setNewOption({ ...newOption, bags: parseInt(e.target.value) || 0 })}
                className="w-full px-3 py-2 bg-white/10 rounded text-white border border-white/20"
              />
            </div>

            <div>
              <label className="block text-white text-sm mb-2">Size (oz)</label>
              <input
                type="number"
                value={newOption.size_oz}
                onChange={(e) => setNewOption({ ...newOption, size_oz: parseInt(e.target.value) || 0 })}
                className="w-full px-3 py-2 bg-white/10 rounded text-white border border-white/20"
              />
            </div>

            {newOption.is_subscription ? (
              <div>
                <label className="block text-white text-sm mb-2">Monthly Price ($)</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={newOption.monthly_price || ''}
                  onChange={(e) => setNewOption({ ...newOption, monthly_price: e.target.value === '' ? null : parseFloat(e.target.value) })}
                  className="w-full px-3 py-2 bg-white/10 rounded text-white border border-white/20"
                />
              </div>
            ) : (
              <div>
                <label className="block text-white text-sm mb-2">Single Price ($)</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={newOption.single_price || ''}
                  onChange={(e) => setNewOption({ ...newOption, single_price: e.target.value === '' ? null : parseFloat(e.target.value) })}
                  className="w-full px-3 py-2 bg-white/10 rounded text-white border border-white/20"
                />
              </div>
            )}

            <div>
              <label className="block text-white text-sm mb-2">Label</label>
              <input
                type="text"
                value={newOption.label}
                onChange={(e) => setNewOption({ ...newOption, label: e.target.value })}
                className="w-full px-3 py-2 bg-white/10 rounded text-white border border-white/20"
              />
            </div>

            <div>
              <label className="block text-white text-sm mb-2">Savings Text</label>
              <input
                type="text"
                value={newOption.savings_text}
                onChange={(e) => setNewOption({ ...newOption, savings_text: e.target.value })}
                className="w-full px-3 py-2 bg-white/10 rounded text-white border border-white/20"
              />
            </div>

            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 text-white">
                <input
                  type="checkbox"
                  checked={newOption.popular}
                  onChange={(e) => setNewOption({ ...newOption, popular: e.target.checked })}
                  className="text-[#fda36b]"
                />
                Popular
              </label>
              <label className="flex items-center gap-2 text-white">
                <input
                  type="checkbox"
                  checked={newOption.active}
                  onChange={(e) => setNewOption({ ...newOption, active: e.target.checked })}
                  className="text-[#fda36b]"
                />
                Active
              </label>
            </div>
          </div>

          <div className="flex gap-4">
            <button
              onClick={onCreateOption}
              disabled={loading}
              className="bg-[#fda36b] text-[#132947] px-4 py-2 rounded-lg hover:bg-[#fda36b]/90 disabled:opacity-50 font-semibold"
            >
              {loading ? 'Creating...' : 'Create'}
            </button>
            <button
              onClick={() => setShowCreateForm(false)}
              className="bg-white/10 text-white px-4 py-2 rounded-lg hover:bg-white/20"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className="space-y-4">
        {options.map((option) => (
          <div key={option.id} className="bg-white/5 p-4 rounded-lg border border-white/10">
            {editingId === option.id && editOption ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {/* Edit form - similar to create form but with editOption */}
                <div>
                  <label className="block text-white text-sm mb-2">Type</label>
                  <div className="flex gap-4">
                    <label className="flex items-center gap-2 text-white">
                      <input
                        type="radio"
                        checked={editOption.is_subscription}
                        onChange={() => setEditOption({ ...editOption, is_subscription: true })}
                        className="text-[#fda36b]"
                      />
                      Subscription
                    </label>
                    <label className="flex items-center gap-2 text-white">
                      <input
                        type="radio"
                        checked={!editOption.is_subscription}
                        onChange={() => setEditOption({ ...editOption, is_subscription: false })}
                        className="text-[#fda36b]"
                      />
                      Single Purchase
                    </label>
                  </div>
                </div>

                {/* Similar inputs as create form but using editOption */}
                <div>
                  <label className="block text-white text-sm mb-2">Months</label>
                  <input
                    type="number"
                    value={editOption.months}
                    onChange={(e) => setEditOption({ ...editOption, months: parseInt(e.target.value) || 0 })}
                    className="w-full px-3 py-2 bg-white/10 rounded text-white border border-white/20"
                  />
                </div>

                <div className="flex gap-2 items-end">
                  <button
                    onClick={onSaveOption}
                    disabled={loading}
                    className="bg-green-600 text-white px-3 py-2 rounded hover:bg-green-700 disabled:opacity-50 flex items-center gap-1"
                  >
                    <Save className="w-4 h-4" />
                    {loading ? 'Saving...' : 'Save'}
                  </button>
                  <button
                    onClick={() => {
                      setEditingId(null);
                      setEditOption(null);
                    }}
                    className="bg-gray-600 text-white px-3 py-2 rounded hover:bg-gray-700 flex items-center gap-1"
                  >
                    <X className="w-4 h-4" />
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex justify-between items-center">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <span className={`px-2 py-1 rounded text-xs font-semibold ${
                      option.is_subscription 
                        ? 'bg-blue-500/20 text-blue-400' 
                        : 'bg-green-500/20 text-green-400'
                    }`}>
                      {option.is_subscription ? 'Subscription' : 'Single Purchase'}
                    </span>
                    {option.popular && (
                      <span className="bg-[#fda36b]/20 text-[#fda36b] px-2 py-1 rounded text-xs font-semibold">
                        Popular
                      </span>
                    )}
                    {!option.active && (
                      <span className="bg-red-500/20 text-red-400 px-2 py-1 rounded text-xs font-semibold">
                        Inactive
                      </span>
                    )}
                  </div>
                  <div className="text-white">
                    <div className="font-semibold">
                      {option.is_subscription ? (
                        `${option.months} months - ${formatPrice(option.monthly_price)}/month`
                      ) : (
                        `${option.bags} bag${option.bags > 1 ? 's' : ''} - ${formatPrice(option.single_price)}`
                      )}
                    </div>
                    <div className="text-white/80 text-sm">
                      {option.bags} bag{option.bags > 1 ? 's' : ''} of {option.size_oz}oz coffee
                    </div>
                    {option.savings_text && (
                      <div className="text-[#fda36b] text-sm font-semibold">
                        {option.savings_text}
                      </div>
                    )}
                    {option.label && (
                      <div className="text-white/60 text-sm">
                        {option.label}
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleEdit(option)}
                    className="bg-blue-600 text-white p-2 rounded hover:bg-blue-700"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => onDeleteOption(option.id)}
                    className="bg-red-600 text-white p-2 rounded hover:bg-red-700"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

// Order Detail Modal Component
interface OrderDetailModalProps {
  order: Order;
  onClose: () => void;
  formatPrice: (cents: number | null) => string;
  formatDate: (dateString: string) => string;
}

const OrderDetailModal: React.FC<OrderDetailModalProps> = ({ order, onClose, formatPrice, formatDate }) => {
  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 z-[60]">
      <div className="bg-[#132947] rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center p-6 border-b border-white/10">
          <h3 className="text-2xl font-bold text-white">Order Details</h3>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
          >
            <X className="w-6 h-6 text-white" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Customer Info */}
          <div className="bg-white/5 p-4 rounded-lg border border-white/10">
            <h4 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
              <Users className="w-5 h-5 text-[#fda36b]" />
              Customer Information
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-white/60 text-sm">Name</p>
                <p className="text-white font-medium">{order.name}</p>
              </div>
              <div>
                <p className="text-white/60 text-sm">Email</p>
                <p className="text-white font-medium">{order.email}</p>
              </div>
              <div>
                <p className="text-white/60 text-sm">Phone</p>
                <p className="text-white font-medium">{order.phone}</p>
              </div>
            </div>
          </div>

          {/* Shipping Address */}
          <div className="bg-white/5 p-4 rounded-lg border border-white/10">
            <h4 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
              <MapPin className="w-5 h-5 text-[#fda36b]" />
              Shipping Address
            </h4>
            <div className="text-white">
              <p>{order.address}</p>
              <p>{order.city}, {order.state} {order.zip_code}</p>
            </div>
          </div>

          {/* Order Details */}
          <div className="bg-white/5 p-4 rounded-lg border border-white/10">
            <h4 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
              <Package className="w-5 h-5 text-[#fda36b]" />
              Order Details
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-white/60 text-sm">Roast Type</p>
                <p className="text-white font-medium capitalize">{order.roast_type}</p>
              </div>
              <div>
                <p className="text-white/60 text-sm">Purchase Type</p>
                <p className="text-white font-medium capitalize">{order.purchase_type}</p>
              </div>
              <div>
                <p className="text-white/60 text-sm">Quantity</p>
                <p className="text-white font-medium">{order.bags} bag{order.bags > 1 ? 's' : ''} × {order.size_oz}oz</p>
              </div>
              {order.purchase_type === 'subscription' && (
                <div>
                  <p className="text-white/60 text-sm">Duration</p>
                  <p className="text-white font-medium">{order.months} months</p>
                </div>
              )}
              <div>
                <p className="text-white/60 text-sm">Total Amount</p>
                <p className="text-white font-bold text-lg">{formatPrice(order.total_due)}</p>
              </div>
            </div>
          </div>

          {/* Status & Dates */}
          <div className="bg-white/5 p-4 rounded-lg border border-white/10">
            <h4 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
              <Calendar className="w-5 h-5 text-[#fda36b]" />
              Status & Timeline
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-white/60 text-sm">Order Status</p>
                <span className={`inline-block px-2 py-1 rounded text-xs font-semibold ${
                  order.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
                  order.status === 'confirmed' ? 'bg-blue-500/20 text-blue-400' :
                  order.status === 'processing' ? 'bg-purple-500/20 text-purple-400' :
                  order.status === 'shipped' ? 'bg-orange-500/20 text-orange-400' :
                  order.status === 'delivered' ? 'bg-green-500/20 text-green-400' :
                  'bg-gray-500/20 text-gray-400'
                }`}>
                  {order.status}
                </span>
              </div>
              <div>
                <p className="text-white/60 text-sm">Payment Status</p>
                <span className={`inline-block px-2 py-1 rounded text-xs font-semibold ${
                  order.payment_status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
                  order.payment_status === 'paid' ? 'bg-green-500/20 text-green-400' :
                  order.payment_status === 'failed' ? 'bg-red-500/20 text-red-400' :
                  'bg-gray-500/20 text-gray-400'
                }`}>
                  {order.payment_status}
                </span>
              </div>
              <div>
                <p className="text-white/60 text-sm">Order Date</p>
                <p className="text-white font-medium">{formatDate(order.created_at)}</p>
              </div>
              <div>
                <p className="text-white/60 text-sm">Last Updated</p>
                <p className="text-white font-medium">{formatDate(order.updated_at)}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminPanel;